<template>
  <div :class="containerClass">
    <div v-if="!embedded" class="flex items-center justify-between mb-3">
      <div class="flex items-center gap-2">
        <span class="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold"
          :class="['bg-' + color + '-100', 'text-' + color + '-900', 'border', 'border-' + color + '-200']">
          {{ title }}
        </span>
        <span class="text-xs text-gray-500">
          {{ rangeLabel }}
        </span>
      </div>
      <div class="text-xs text-gray-400">
        {{ totalLabel }}
      </div>
    </div>

    <div class="overflow-x-auto">
      <div class="inline-grid gap-1" :style="gridStyle">
        <template v-for="cell in cells" :key="cell.key">
          <div
            class="rounded-sm"
            :class="[cellSizeClass, cellClass(cell)]"
            :title="cell.title"
          />
        </template>
      </div>
    </div>

    <div v-if="!embedded" class="mt-3 flex items-center justify-end gap-2 text-[10px] text-gray-500">
      <span>0%</span>
      <div class="w-3 h-3 rounded-sm bg-gray-100 border border-gray-200" />
      <div class="w-3 h-3 rounded-sm" :class="levelLegendClass(1)" />
      <div class="w-3 h-3 rounded-sm" :class="levelLegendClass(2)" />
      <div class="w-3 h-3 rounded-sm" :class="levelLegendClass(3)" />
      <div class="w-3 h-3 rounded-sm" :class="levelLegendClass(4)" />
      <span>≥100%</span>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

function clampNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export default {
  name: 'HabitHeatmap',
  props: {
    title: { type: String, required: true },
    color: { type: String, default: 'gray' },
    from: { type: Number, required: true },
    to: { type: Number, required: true },
    tz: { type: String, default: dayjs.tz.guess() },
    totalsByDate: { type: Object, default: () => ({}) },
    goal: { type: Number, default: 0 },
    totalAll: { type: Number, default: 0 },
    embedded: { type: Boolean, default: false },
    layout: { type: String, default: 'calendar' },
  },
  computed: {
    effectiveLayout() {
      if (this.embedded) return 'compact';
      return this.layout;
    },
    containerClass() {
      if (this.embedded) return 'bg-transparent p-0 border-0 shadow-none';
      return 'bg-white rounded-lg shadow border p-4';
    },
    cellSizeClass() {
      return this.embedded ? 'w-2 h-2' : 'w-3 h-3';
    },
    rangeLabel() {
      const fromLabel = dayjs(this.from).tz(this.tz).format('DD MMM YYYY');
      const toLabel = dayjs(this.to).tz(this.tz).format('DD MMM YYYY');
      return `${fromLabel} – ${toLabel}`;
    },
    totalLabel() {
      const total = clampNumber(this.totalAll, 0);
      const goal = clampNumber(this.goal, 0);
      if (goal > 0 && total) return `Total: ${total} (Goal: ${goal}/day)`;
      if (goal > 0) return `Goal: ${goal}/day`;
      return total ? `Total: ${total}` : '';
    },
    cells() {
      const fromDay = dayjs(this.from).tz(this.tz).startOf('day');
      const toDay = dayjs(this.to).tz(this.tz).endOf('day');

      if (this.effectiveLayout === 'compact') {
        const out = [];
        let cursor = fromDay;
        while (cursor.valueOf() <= toDay.valueOf()) {
          const key = cursor.format('YYYY-MM-DD');
          const total = clampNumber(this.totalsByDate[key], 0);
          const goal = clampNumber(this.goal, 0);

          let title = '';
          if (goal > 0) {
            const pct = total ? Math.round((total / goal) * 100) : 0;
            title = `${key}: ${total} / ${goal} (${pct}%)`;
          } else {
            title = `${key}: ${total}`;
          }

          out.push({
            key,
            dateKey: key,
            inRange: true,
            total,
            goal,
            title,
          });

          cursor = cursor.add(1, 'day');
        }
        return out;
      }

      const gridStart = fromDay.startOf('week');
      const gridEnd = toDay.endOf('week');

      const out = [];
      let cursor = gridStart;
      while (cursor.valueOf() <= gridEnd.valueOf()) {
        const key = cursor.format('YYYY-MM-DD');
        const inRange = cursor.valueOf() >= fromDay.valueOf() && cursor.valueOf() <= toDay.valueOf();
        const total = clampNumber(this.totalsByDate[key], 0);
        const goal = clampNumber(this.goal, 0);

        let title = '';
        if (inRange) {
          if (goal > 0) {
            const pct = Math.round((total / goal) * 100);
            title = `${key}: ${total} / ${goal} (${pct}%)`;
          } else {
            title = `${key}: ${total}`;
          }
        }

        out.push({
          key,
          dateKey: key,
          inRange,
          total,
          goal,
          title,
        });

        cursor = cursor.add(1, 'day');
      }
      return out;
    },
    weeksCount() {
      if (!this.cells.length) return 0;
      return Math.ceil(this.cells.length / 7);
    },
    gridStyle() {
      if (this.effectiveLayout === 'compact') {
        // Sequential squares, no weekday alignment
        const cols = this.embedded ? 16 : 20;
        return {
          gridAutoFlow: 'row',
          gridTemplateColumns: `repeat(${cols}, min-content)`,
        };
      }

      // 7 rows (days), N columns (weeks)
      return {
        gridTemplateRows: 'repeat(7, minmax(0, 1fr))',
        gridAutoFlow: 'column',
        gridAutoColumns: 'min-content',
      };
    },
  },
  methods: {
    safeColor() {
      return (typeof this.color === 'string' && this.color.trim()) ? this.color.trim() : 'green';
    },
    levelLegendClass(level) {
      const c = this.safeColor();
      if (level === 1) return [`bg-${c}-100`, `border`, `border-${c}-900`];
      if (level === 2) return [`bg-${c}-200`, `border`, `border-${c}-900`];
      if (level === 3) return [`bg-${c}-300`, `border`, `border-${c}-900`];
      return [`bg-${c}-500`, `border`, `border-${c}-900`];
    },
    levelForCell(total, goal) {
      if (!total || total <= 0) return 0;
      if (!goal || goal <= 0) return 4;

      const ratio = total / goal;
      if (ratio >= 1) return 4;
      if (ratio >= 0.75) return 3;
      if (ratio >= 0.5) return 2;
      return 1;
    },
    cellClass(cell) {
      if (!cell.inRange) return 'bg-transparent';

      const c = this.safeColor();

      const level = this.levelForCell(cell.total, cell.goal);
      if (level === 0) return `bg-gray-100 border border-${c}-900`;
      if (level === 1) return `bg-${c}-100 border border-${c}-900`;
      if (level === 2) return `bg-${c}-200 border border-${c}-900`;
      if (level === 3) return `bg-${c}-300 border border-${c}-900`;
      return `bg-${c}-500 border border-${c}-900`;
    },
  },
};
</script>
